import React, { useState, useCallback, useRef, useEffect } from 'react';
import { MarketplaceSellerProfile } from '../../../slices/profiles-data-slice/models/marketplace-seller-profile';
import { OwnershipTypeOptions } from '../../../slices/marketplace/timeline/models/enums/ownership-type.enum';
import {
    LocationTypeOptions,
    locationTypeTooltipHtml,
} from '../../../slices/marketplace/timeline/models/enums/location-type.enum';
import InfoTooltip from '../../info-tooltip/info-tooltip';
import { RevenueGrowthRateOptions } from '../../../slices/marketplace/timeline/models/enums/revenue-growth.enum';
import './AnonymizedProfile.scss';
import { useServiceFocuses } from '../../../custom-hooks/useServiceFocuses';
import { StarRating } from '../../StarRating/StarRating';
import {
    mapServiceFocusesToOptions,
    isOtherFocus,
    getOtherFocus,
} from '../../../containers/UserPages/Login/Modals/PracticeRegistrationForm/utils';
import { NotificationFlag } from '../../NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../store/selectors';
import { RefreshNotificationFlags } from '../../../slices/notification-flags/refresh-notification-flags';

interface AnonymizedProfileProps {
    profileData?: MarketplaceSellerProfile;
    timelineId?: number;
}

const AnonymizedProfile: React.FC<AnonymizedProfileProps> = (props) => {
    const { profileData, timelineId } = props;
    const serviceFocuses = useServiceFocuses();
    const serviceFocusOptions = mapServiceFocusesToOptions(serviceFocuses);
    const [activePracticeIndex, setActivePracticeIndex] = useState(0);

    const practices = profileData?.practiceProfiles || [];
    const activePractice = practices[activePracticeIndex];

    const notificationFlags = useStateSelector(
        (s) => s.notificationFlags.flags
    );
    const axios = useStateSelector((state) => state.core.axios);
    const dispatch = useDispatch() as any;
    const activePracticeIdRef = useRef<string>(practices[0]?.id ?? '');
    const readPracticeNotificationsRef = useRef<(practiceId: string) => void>(
        () => {}
    );

    const readPracticeNotifications = useCallback(
        (practiceId: string) => {
            if (!timelineId || !practiceId) return;
            const prefix = `${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.OpportunitiesCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${practiceId}`;
            const flagsToRead = notificationFlags.filter((f) =>
                f.eventCode.toLowerCase().startsWith(prefix.toLowerCase())
            );
            if (flagsToRead.length === 0) return;
            axios
                .delete('api/NotificationFlags', {
                    data: flagsToRead.map((f) => f.eventCode),
                })
                .then(() => dispatch(RefreshNotificationFlags()))
                .catch(() => {});
        },
        [timelineId, notificationFlags, dispatch, axios]
    );

    readPracticeNotificationsRef.current = readPracticeNotifications;

    useEffect(() => {
        activePracticeIdRef.current = practices[activePracticeIndex]?.id ?? '';
    }, [activePracticeIndex, practices]);

    useEffect(() => {
        return () => {
            readPracticeNotificationsRef.current(activePracticeIdRef.current);
        };
    }, []);

    const renderTabs = () => {
        if (practices.length <= 1) return null;

        return (
            <div className="RRT__container">
                <div className="RRT__tabs">
                    {practices.map((practice, index) => (
                        <div
                            key={practice.id}
                            className={
                                activePracticeIndex === index
                                    ? 'RRT__tab RRT__tab--first RRT__tab--selected anonymized-profile-tab'
                                    : 'RRT__tab anonymized-profile-tab'
                            }
                            onClick={() => {
                                if (index !== activePracticeIndex) {
                                    readPracticeNotifications(
                                        practices[activePracticeIndex]?.id ?? ''
                                    );
                                }
                                setActivePracticeIndex(index);
                            }}>
                            <span className="title-with-badge">
                                Practice {index + 1}
                                {timelineId && (
                                    <NotificationFlag
                                        doNotRemove={true}
                                        includePrefix={true}
                                        eventCode={`${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.OpportunitiesCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${practice.id}`}
                                    />
                                )}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const practiceFieldBadge = (field: string, includePrefix?: boolean) =>
        timelineId && activePractice ? (
            <NotificationFlag
                doNotRemove={true}
                includePrefix={includePrefix}
                eventCode={`${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.OpportunitiesCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${activePractice.id}.${field}`}
            />
        ) : null;

    const getServiceFocusLabel = (): string => {
        if (!activePractice) return '';

        const isOther = isOtherFocus(
            serviceFocuses,
            activePractice.practiceFocusType
        );

        if (isOther) {
            return (
                activePractice.otherPracticeType ||
                getOtherFocus(serviceFocuses).name ||
                ''
            );
        }

        return serviceFocusOptions[activePractice.practiceFocusType] || '';
    };

    const renderPracticeInfo = () => {
        if (!activePractice) return null;

        return (
            <div className="info-section">
                <div className="section-title">Practice Information</div>
                <div className="info-grid">
                    <div className="info-item">
                        <span className="label-with-badge">
                            Ownership Type
                            {practiceFieldBadge('PracticeOwnershipType')}
                        </span>
                        <span className="value">
                            {activePractice.practiceOwnershipType
                                ? OwnershipTypeOptions[
                                      activePractice.practiceOwnershipType
                                  ]
                                : ''}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label-with-badge">
                            Service Focus
                            {practiceFieldBadge('PracticeFocusType')}
                        </span>
                        <span className="value">{getServiceFocusLabel()}</span>
                    </div>
                    <div className="info-item">
                        <span className="label-with-badge">
                            Number of FTE DVMs
                            {practiceFieldBadge('FteDvms')}
                        </span>
                        <span className="value">
                            {activePractice.fteDvms || ''}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label-with-badge">
                            Revenue Growth Rate
                            {practiceFieldBadge('RevenueGrowthRate')}
                        </span>
                        <span className="value">
                            {activePractice.revenueGrowthRate
                                ? RevenueGrowthRateOptions[
                                      activePractice.revenueGrowthRate
                                  ]
                                : ''}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label-with-badge">
                            Location Type
                            <InfoTooltip
                                idText="location-type-anon-tooltip"
                                text=""
                                html={locationTypeTooltipHtml}
                                place="top"
                            />
                            {practiceFieldBadge('PracticeLocations', true)}
                        </span>
                        <span className="value">
                            {activePractice.practiceLocations?.[0]?.locationType
                                ? LocationTypeOptions[
                                      activePractice.practiceLocations[0]
                                          .locationType
                                  ]
                                : ''}
                        </span>
                    </div>
                    {activePractice?.averageRating != null && (
                        <div className="info-item">
                            <span className="label-with-badge">
                                Average Rating
                                {practiceFieldBadge('AverageRating')}
                            </span>
                            <span className="value">
                                <StarRating
                                    value={activePractice.averageRating}
                                    size="md"
                                />
                            </span>
                        </div>
                    )}
                    {activePractice.ownerPostClosingInvolvementAndConsiderationFlexibility && (
                        <div className="info-item">
                            <span className="label-with-badge">
                                Owner Post-Closing Involvement and Consideration Flexibility
                                {practiceFieldBadge('OwnerPostClosingInvolvementAndConsiderationFlexibility')}
                            </span>
                            <span className="value">
                                {activePractice.ownerPostClosingInvolvementAndConsiderationFlexibility}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="anonymized-profile">
            <div className="profile-content">
                {renderTabs()}
                <div className="profile-info">{renderPracticeInfo()}</div>
            </div>
        </div>
    );
};

export default AnonymizedProfile;
